// Classic-chess and pawns-mode commands plus student exercise move validation.
import type { Dispatch, SetStateAction } from 'react';
import { Chess } from 'chess.js';
import { getSocket } from '../socket';
import type { Color, Role, RoomMode, RoomState } from '../types';
import { boardFromFen, type BoardMap, type PieceCode } from '../board/pieces';
import { playTrainingMove, type TrainingStep } from '../lecture';
import type { PendingPromotion } from './lectureActions';

type GameActionContext = {
  roomId: string;
  role: Role;
  state: RoomState | null;
  recording: boolean;
  selectedMode: RoomMode;
  gameMinutes: number;
  gameIncrement: number;
  trainerColor: Color;
  startingTurn: Color;
  board: BoardMap;
  pendingGameMove: boolean;
  exerciseFrozen: boolean;
  exerciseIndex: number;
  steps: TrainingStep[];
  studentExerciseFen: string;
  isPromotionTarget: (piece: PieceCode | undefined, to: string) => boolean;
  setGameOver: Dispatch<SetStateAction<{ winner: Color | 'draw' | null; reason: string | null } | null>>;
  setPendingPromotion: Dispatch<SetStateAction<PendingPromotion | null>>;
  setBoard: Dispatch<SetStateAction<BoardMap>>;
  setPendingGameMove: Dispatch<SetStateAction<boolean>>;
  setSelected: Dispatch<SetStateAction<string | null>>;
  setStatus: Dispatch<SetStateAction<string>>;
};

const socket = getSocket();

export function createGameActions(context: GameActionContext) {
  const {
    roomId, role, state, recording, selectedMode, gameMinutes, gameIncrement, trainerColor,
    startingTurn, board, pendingGameMove, exerciseFrozen, exerciseIndex, steps, studentExerciseFen,
    isPromotionTarget, setGameOver, setPendingPromotion, setBoard, setPendingGameMove,
    setSelected, setStatus
  } = context;

  function finishGame() {
    socket.emit('finishGame', { roomId });
    setGameOver(null);
    setPendingPromotion(null);
  }

  function resetPawnsGame() {
    socket.emit('resetGame', { roomId });
    setGameOver(null);
    setPendingPromotion(null);
  }

  function startGame() {
    const nextMode = selectedMode === 'pawns' ? 'pawns' : 'standard';
    socket.emit('startGame', {
      roomId,
      mode: nextMode,
      minutes: gameMinutes,
      incrementSeconds: gameIncrement,
      trainerColor,
      startingTurn
    });
  }

  function gamePieceColor(): Color {
    return role === 'trainer'
      ? (state?.trainerColor || trainerColor)
      : (state?.trainerColor === 'w' ? 'b' : 'w');
  }

  function canGamePiece(piece: PieceCode | undefined) {
    if (pendingGameMove || !piece || !state || state.mode === 'lecture' || !state.gameTimer.active) return false;
    const mine = gamePieceColor();
    return piece[0] === mine && piece[0] === state.turn;
  }

  function sendGameMove(from: string, to: string, promotion?: string) {
    if (pendingGameMove) return;

    const piece = board[from];
    if (!canGamePiece(piece)) {
      setSelected(null);
      setStatus('Сейчас не ваш ход');
      return;
    }

    let predictedBoard: BoardMap | null = null;
    if (state?.mode === 'standard') {
      const chess = new Chess();
      try {
        chess.load(state.fen, { skipValidation: true });
        const move = chess.move({ from, to, promotion: promotion || 'q' });
        if (!move) {
          setSelected(null);
          setStatus('Недопустимый ход');
          return;
        }
        predictedBoard = boardFromFen(chess.fen());
      } catch {
        setSelected(null);
        setStatus('Недопустимый ход');
        return;
      }
    } else if (state?.mode === 'pawns') {
      const next = { ...board };
      const movingPiece = next[from];
      if (movingPiece) {
        delete next[from];
        next[to] = movingPiece;
        predictedBoard = next;
      }
    }

    if (predictedBoard) setBoard(predictedBoard);
    setPendingGameMove(true);
    socket.emit('makeMove', { roomId, move: from + '-' + to + (promotion ? '-' + promotion : '') });
    setSelected(null);
  }

  function gameMove(from: string, to: string) {
    const piece = board[from];
    if (!canGamePiece(piece)) {
      setSelected(null);
      return;
    }

    if (state?.mode === 'standard' && isPromotionTarget(piece, to)) {
      setPendingPromotion({ from, to, color: piece[0] as Color, context: 'game' });
      return;
    }

    sendGameMove(from, to);
  }

  function studentStep(from: string, to: string, promotion?: 'q' | 'r' | 'b' | 'n') {
    if (!state?.exercise || exerciseFrozen) return;
    if (!state.lectureTimer.active) {
      setSelected(null);
      setStatus('Дождитесь запуска таймера тренером.');
      return;
    }
    const step = steps[exerciseIndex];
    if (!step) {
      setStatus('Задача решена');
      return;
    }

    const sourcePiece = board[from];
    if (!sourcePiece || sourcePiece[0] !== step.color) {
      setSelected(null);
      return;
    }

    if (!promotion && isPromotionTarget(sourcePiece, to)) {
      setPendingPromotion({ from, to, color: sourcePiece[0], context: 'exercise' });
      setSelected(null);
      return;
    }

    const chess = new Chess();
    const requestedMove = from + '-' + to + (promotion ? '-' + promotion : '');
    try {
      chess.load(studentExerciseFen || state.exercise.initialFen, { skipValidation: true });
      const move = playTrainingMove(chess, step.color, requestedMove);

      if (!move) {
        setSelected(null);
        setStatus('Недопустимый ход');
        return;
      }

      socket.emit('studentMove', { roomId, move: requestedMove });
      setSelected(null);
      setStatus('Проверка хода…');
    } catch {
      setSelected(null);
      setStatus('Недопустимый ход');
    }
  }

  function retryExercise() {
    if (role !== 'trainer' || !state || state.mode !== 'lecture' || recording) return;
    socket.emit('retryExercise', { roomId });
  }

  return { finishGame, resetPawnsGame, startGame, canGamePiece, sendGameMove, gameMove, studentStep, retryExercise };
}
